/**
 * SPEC-007 §13/§33/plan §6 — the seven canonical save states. This module
 * is the pure state machine only: no timers, no network, no store access,
 * so it is directly unit-testable (T071) independent of the coordinator
 * that drives it (T072).
 */
export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict' | 'offline';

export type SaveEvent =
  | { type: 'mutate' }
  | { type: 'save_start' }
  | { type: 'save_success'; hasNewerChange: boolean }
  | { type: 'save_conflict' }
  | { type: 'save_error' }
  | { type: 'go_offline' }
  | { type: 'go_online' }
  | { type: 'settle' };

/**
 * A conflict is a hard stop (SAVE-REQ-015): autosave must not resume on its
 * own, so `mutate` while `conflict` intentionally leaves the state
 * unchanged. Everything else about "is there unsaved local state" is
 * tracked by the coordinator's `dirty`/mutation-version bookkeeping, not by
 * this reducer alone.
 */
export function transition(state: SaveState, event: SaveEvent): SaveState {
  switch (event.type) {
    case 'mutate':
      if (state === 'conflict') return state;
      return state === 'offline' ? 'offline' : 'dirty';
    case 'save_start':
      return 'saving';
    case 'save_success':
      return event.hasNewerChange ? 'dirty' : 'saved';
    case 'save_conflict':
      return 'conflict';
    case 'save_error':
      return 'error';
    case 'go_offline':
      return 'offline';
    case 'go_online':
      return state === 'offline' ? 'dirty' : state;
    case 'settle':
      return state === 'saved' ? 'clean' : state;
    default:
      return state;
  }
}

export const SAVE_STATE_COPY: Record<SaveState, string> = {
  clean: 'Salvo',
  dirty: 'Alterações não salvas',
  saving: 'Salvando…',
  saved: 'Salvo',
  error: 'Falha ao salvar',
  conflict: 'Conflito detectado',
  offline: 'Sem conexão',
};

/** SAVE-REQ-021/AC-042 — states where losing in-memory work is a real risk. */
export function isUnsafeToLoseState(state: SaveState): boolean {
  return state === 'dirty' || state === 'saving' || state === 'error' || state === 'offline' || state === 'conflict';
}
